import React, { useState } from 'react';
import { X, Check, XCircle, LogOut, Download, Eye, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { registrationAPI, getFileURL } from '../services/api';

const AdminPanel = ({ isOpen, onClose, registrations, onVerify, onReject, onLogout }) => {
  const [selectedReg, setSelectedReg] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  if (!isOpen) return null;

  const pendingRegistrations = registrations.filter(r => r.status === 'pending');
  const verifiedRegistrations = registrations.filter(r => r.status === 'verified');
  const rejectedRegistrations = registrations.filter(r => r.status === 'rejected');

  const handleLogout = () => {
    if (window.confirm('Yakin ingin logout dari admin panel?')) {
      onLogout();
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      await registrationAPI.exportExcel();
      alert('Data berhasil diekspor ke Excel!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Gagal mengekspor data.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Admin Panel</h2>
          <div className="flex items-center gap-1 sm:gap-3">
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to Excel"
            >
              <Download className="w-4 sm:w-5 h-4 sm:h-5" />
              <span className="hidden sm:inline text-sm font-medium">
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
              title="Logout"
            >
              <LogOut className="w-4 sm:w-5 h-4 sm:h-5" />
              <span className="hidden sm:inline text-sm font-medium">Logout</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          {/* Statistics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-300 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-yellow-500 p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-yellow-700 mb-1">Menunggu Verifikasi</p>
                  <p className="text-3xl font-bold text-yellow-900">{pendingRegistrations.length}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-yellow-300">
                <p className="text-xs text-yellow-700">Perlu ditinjau segera</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-green-500 p-3 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-green-700 mb-1">Terverifikasi</p>
                  <p className="text-3xl font-bold text-green-900">{verifiedRegistrations.length}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-green-300">
                <p className="text-xs text-green-700">Siswa aktif terdaftar</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-red-500 p-3 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-red-700 mb-1">Ditolak</p>
                  <p className="text-3xl font-bold text-red-900">{rejectedRegistrations.length}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-red-300">
                <p className="text-xs text-red-700">Pendaftaran tidak valid</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b mb-4">
            <div className="flex gap-4">
              <button className="px-4 py-2 border-b-2 border-blue-600 text-blue-600 font-medium">
                Pending ({pendingRegistrations.length})
              </button>
            </div>
          </div>

          {/* Registrations List */}
          <div className="space-y-4">
            {pendingRegistrations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Tidak ada pendaftaran yang menunggu verifikasi
              </div>
            ) : (
              pendingRegistrations.map((reg) => (
                <div key={reg.id} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                    <div className="flex-1 w-full">
                      <h3 className="font-semibold text-base sm:text-lg">{reg.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 break-all">{reg.email}</p>
                      <p className="text-xs sm:text-sm text-gray-600">{reg.phone}</p>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-blue-600">
                          {reg.program_title || reg.program?.title || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {reg.program_date || reg.program?.date || 'N/A'} • {reg.program_price || reg.program?.price || 'N/A'}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Dikirim: {new Date(reg.submitted_at || reg.submittedAt).toLocaleString('id-ID')}
                      </p>
                    </div>

                    <div className="flex sm:flex-col flex-row gap-2 w-full sm:w-auto sm:ml-4">
                      <button
                        onClick={() => {
                          setSelectedReg(reg);
                          setShowDetailModal(true);
                        }}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">Lihat Detail</span>
                      </button>
                      <button
                        onClick={() => onVerify(reg.id)}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2 text-sm"
                      >
                        <Check className="w-4 h-4" />
                        <span className="hidden sm:inline">Verifikasi</span>
                      </button>
                      <button
                        onClick={() => onReject(reg.id)}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2 text-sm"
                      >
                        <XCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">Tolak</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Verified and Rejected Lists */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {/* Verified List */}
            {verifiedRegistrations.length > 0 && (
              <div>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-green-900">
                  <CheckCircle className="w-5 h-5" />
                  Pendaftaran Terverifikasi ({verifiedRegistrations.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {verifiedRegistrations.map((reg) => (
                    <div key={reg.id} className="border-2 border-green-200 bg-green-50 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-green-900">{reg.name}</p>
                          <p className="text-sm text-green-700 mt-1">
                            {reg.program_title || reg.program?.title || 'N/A'}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            {reg.email}
                          </p>
                        </div>
                        <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                          ✓ Verified
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejected List */}
            {rejectedRegistrations.length > 0 && (
              <div>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-red-900">
                  <AlertCircle className="w-5 h-5" />
                  Pendaftaran Ditolak ({rejectedRegistrations.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {rejectedRegistrations.map((reg) => (
                    <div key={reg.id} className="border-2 border-red-200 bg-red-50 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-red-900">{reg.name}</p>
                          <p className="text-sm text-red-700 mt-1">
                            {reg.program_title || reg.program?.title || 'N/A'}
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            {reg.email}
                          </p>
                        </div>
                        <span className="bg-red-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                          ✗ Rejected
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedReg && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[60] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-700 to-blue-600 text-white px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center rounded-t-2xl">
              <h2 className="text-lg sm:text-2xl font-bold">Detail Pendaftaran</h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedReg(null);
                }}
                className="text-white hover:bg-blue-800 rounded-full p-2 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Student Info */}
              <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-blue-900 mb-4">Informasi Siswa</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-blue-600 font-medium mb-1">Nama Lengkap</p>
                    <p className="text-base text-blue-900 font-semibold">{selectedReg.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium mb-1">Email</p>
                    <p className="text-base text-blue-900">{selectedReg.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium mb-1">No. WhatsApp</p>
                    <p className="text-base text-blue-900">{selectedReg.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium mb-1">Waktu Pendaftaran</p>
                    <p className="text-base text-blue-900">
                      {new Date(selectedReg.submitted_at || selectedReg.submittedAt).toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Program Info */}
              <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-blue-900 mb-4">Informasi Program</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-blue-600 font-medium mb-1">Nama Program</p>
                    <p className="text-base text-blue-900 font-semibold">
                      {selectedReg.program_title || selectedReg.program?.title || 'N/A'}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-blue-600 font-medium mb-1">Modul</p>
                      <p className="text-base text-blue-900">
                        {selectedReg.program_module || selectedReg.program?.module || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium mb-1">Track</p>
                      <p className="text-base text-blue-900">
                        {selectedReg.program_track || selectedReg.program?.track || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium mb-1">Jadwal</p>
                      <p className="text-base text-blue-900">
                        {selectedReg.program_date || selectedReg.program?.date || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium mb-1">Harga</p>
                      <p className="text-base text-blue-900 font-bold">
                        {selectedReg.program_price || selectedReg.program?.price || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Proof */}
              <div className="bg-green-50 rounded-xl p-5 border-2 border-green-200">
                <h3 className="text-lg font-bold text-green-900 mb-4">Bukti Pembayaran</h3>
                {(selectedReg.payment_proof_name || selectedReg.paymentProofName) ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-green-600 font-medium mb-2">Nama File</p>
                      <p className="text-base text-green-900 bg-white px-4 py-2 rounded-lg border border-green-300">
                        📎 {selectedReg.payment_proof_name || selectedReg.paymentProofName}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-green-300 p-4">
                      <p className="text-sm text-green-600 font-medium mb-3">Preview Bukti Transfer</p>
                      <div className="rounded-lg overflow-hidden border-2 border-green-200">
                        <img
                          src={getFileURL(`/uploads/payments/${selectedReg.payment_proof_name || selectedReg.paymentProofName}`)}
                          alt="Bukti Pembayaran"
                          className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%239ca3af"%3EGambar tidak dapat dimuat%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                      <div className="mt-3 text-center">
                        <a
                          href={getFileURL(`/uploads/payments/${selectedReg.payment_proof_name || selectedReg.paymentProofName}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-green-700 hover:text-green-900 font-medium hover:underline"
                        >
                          <Download className="w-4 h-4" />
                          Download Bukti Pembayaran
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Tidak ada bukti pembayaran</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={() => {
                    onVerify(selectedReg.id);
                    setShowDetailModal(false);
                    setSelectedReg(null);
                  }}
                  className="flex-1 bg-green-600 text-white px-4 sm:px-6 py-3 rounded-xl hover:bg-green-700 transition font-bold flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Check className="w-5 h-5" />
                  Verifikasi Pendaftaran
                </button>
                <button
                  onClick={() => {
                    onReject(selectedReg.id);
                    setShowDetailModal(false);
                    setSelectedReg(null);
                  }}
                  className="flex-1 bg-red-600 text-white px-4 sm:px-6 py-3 rounded-xl hover:bg-red-700 transition font-bold flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <XCircle className="w-5 h-5" />
                  Tolak Pendaftaran
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
